if (!customElements.get('collection-page')) {
  class CollectionPage extends HTMLElement {
    constructor() {
      super();
      this.initElements();
      this.initState();
      this.bindEvents();
    }

    initElements() {
      // Cache all DOM elements
      this.els = {
        form: this.querySelector('.js-filter-form'),
        grid: this.querySelector('.js-collection-grid'),
        list: this.querySelector('.js-list-view'),
        sortToggle: this.querySelector('.js-sortby'),
        sortLabel: this.querySelector('.js-sortby-label'),
        defaultSort: this.querySelector('.js-filter-sortby.is-default'),
        loadMore: this.querySelector('.js-loadMore-btn'),
        loadPrev: this.querySelector('.js-loadPrevious-btn'),
        loadContainer: this.querySelector('.js-loadmore-container'),
        processBar: this.querySelector('.js-collection-process'),
        currentQty: this.querySelector('.js-collection-currentQty'),
        totalQty: this.querySelector('.js-collection-totalQty'),
        qtyInfo: this.querySelector('.js-collection-info'),
        drawer: this.querySelector('.js-filter-drawer'),
        drawerToggle: this.querySelector('.js-drawer-toggle'),
        drawerOpen: this.querySelector('.js-drawer-open'),
        overlay: this.querySelector('.js-filter-overlay'),
        submit: this.querySelector('.js-filter-submit'),
        close: this.querySelector('.js-filter-close'),
        search: this.querySelector('.js-filter-search'),
        clearInput: this.querySelector('.js-clear-input'),
        priceMin: this.querySelector('.js-filter-price-min'),
        priceMax: this.querySelector('.js-filter-price-max'),
        priceLabelMin: this.querySelector('.js-price-label-min'),
        priceLabelMax: this.querySelector('.js-price-label-max'),
        sizeFilter: this.querySelector('.js-top-filter')
      };

      this.lists = {
        sortInputs: this.querySelectorAll('.js-filter-sortby'),
        filters: this.querySelectorAll('.js-filter, .collection__size-options'),
        inputs: this.querySelectorAll('.js-filter-input, .js-filter-price-max, .js-filter-price-min, .js-size-filter-input'),
        toggles: this.querySelectorAll('.js-filter-toggle, .js-filters-toggle'),
        showMore: this.querySelectorAll('.js-filter-showMore'),
        priceInputs: this.querySelectorAll('.js-price-range'),
        countActive: this.querySelectorAll('.js-count-active-filter'),
        productTypes: this.querySelectorAll('.js-product-type')
      };
    }

    initState() {
      this.filterData = [];
      this.searchParamsInitial = window.location.search.slice(1);
      this.searchParamsPrev = window.location.search.slice(1);
      
      const btn = this.els.loadMore;
      this.productPerPage = Number(btn?.dataset.perPage);
      this.maxProduct = Number(btn?.dataset.maxProduct);
      this.currentProductQty = Number(btn?.dataset.perPage);
      this.maxPage = Number(btn?.dataset.maxPage);
      this.currentPage = Number(btn?.dataset.currentPage || 1);
      this.previousPages = this.currentPage;
      this.disableInfinite = this.dataset.disableInfinite;
    }

    bindEvents() {
      this.setupFormSubmit();
      this.setupFilterDrawer();
      this.setupClearFilters();
      this.setupFilterToggles();
      this.setupShowMore();
      this.setupPriceRange();
      this.setupSearch();
      this.setupSortBy();
      this.setupSizeFilter();
      this.setupCategories();
      
      if (!this.disableInfinite) this.setupInfiniteScroll();
      
      window.addEventListener('popstate', (e) => {
        const params = e.state?.searchParams || this.searchParamsInitial;
        if (params !== this.searchParamsPrev) this.renderPage(params, null, false);
      });

      this.updateFilterLabels();
      this.autoOpenFilters();
      this.stickyFilterButton();
    }

    // Form submission
    setupFormSubmit() {
      if (!this.els.form) return;

      const debouncedSubmit = debounce(async (e) => {
        this.syncFilterSwatches(e);
        const params = this.createSearchParams(this.els.form);

        if (window.innerWidth > 1024) {
          await Promise.all([
            this.renderPage(params, e),
            this.updateSizeFilterOnly(params)
          ]);
          this.loadSecondPageWhenScrolling();
        }
      }, 500);

      this.els.form.addEventListener('input', debouncedSubmit);

      this.els.submit?.addEventListener('click', async (e) => {
        if (window.innerWidth <= 1024) {
          e.preventDefault();
          const params = this.createSearchParams(this.els.form);
          await Promise.all([
            this.renderPage(params, e),
            this.updateSizeFilterOnly(params)
          ]);
          this.loadMoreHtml(true);
          this.toggleDrawer();
        }
      });
    }

    syncFilterSwatches(e) {
      const { filter, collapsed } = e.target.dataset;
      const isChecked = e.target?.checked;

      if (!filter || collapsed !== 'true') return;

      this.querySelectorAll(`.js-filter-input[data-filter="${filter}"]`)
        .forEach(f => f.checked = isChecked);
    }

    // Rendering
    async renderPage(searchParams, event, updateURL = true) {
      if (searchParams === this.searchParamsPrev) return;

      this.searchParamsPrev = this.clearSearchParams(searchParams);
      this.toggleClearButton();

      const url = `${this.dataset.urlHandle}?${searchParams}`;
      const cached = this.filterData.find(d => d.url === url);

      cached ? this.renderFromCache(cached) : await this.renderFromFetch(url);

      if (updateURL) this.updateURL(searchParams);
      this.scrollToCollection();
    }

    async renderFromFetch(url) {
      this.setLoading(true);

      try {
        const response = await fetch(url, { 
          headers: { 'Content-Type': 'text/plain' } 
        });
        const html = await response.text();

        this.filterData.push({ html, url });
        this.updateDOM(html);
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    renderFromCache(cached) {
      this.setLoading(true);
      this.updateDOM(cached.html);
      this.setLoading(false);
    }

    updateDOM(html) {
      const dom = new DOMParser().parseFromString(html, 'text/html');

      this.updateSection('.js-collection-grid', dom);
      this.updateSection('.js-list-view', dom);
      this.updateSection('.js-remove-filter-tags', dom);
      this.updateQuantityInfo(dom);
      this.updateFilterInputs(dom);
      this.updateCountActive(dom);
      this.updateSizeFilterSection(dom);
      this.callUpdateMedia();
      this.refreshExternalScripts();
    }

    updateSection(selector, dom) {
      const newContent = dom.querySelector(selector);
      const current = this.querySelector(selector);
      
      if (newContent && current) {
        current.innerHTML = newContent.innerHTML;
        this.clearFilterCache();
      }
    }

    updateQuantityInfo(dom) {
      const container = dom.querySelector('.js-loadmore-container');
      
      if (container) {
        this.els.loadContainer?.classList.remove('hidden');
        
        this.maxProduct = Number(container.querySelector('.js-collection-totalQty').textContent);
        this.currentProductQty = Number(container.querySelector('.js-collection-currentQty').textContent);
        this.maxPage = Number(container.querySelector('.js-loadMore-btn').dataset.maxPage);
        this.currentPage = Number(container.querySelector('.js-loadMore-btn').dataset.currentPage);

        this.updateLoadMoreUI();
      } else if (this.currentProductQty >= this.maxProduct) {
        this.els.loadContainer?.classList.add('hidden');
      }
    }

    updateFilterInputs(dom) {
      const params = new URLSearchParams(this.searchParamsPrev);

      this.lists.inputs.forEach(input => {
        input.checked = params.has(input.name, input.value);
        
        const text = input.parentElement.querySelector(`.js-filter-text[data-id="${input.id}"]`);
        const newText = dom.querySelector(`.js-filter-text[data-id="${input.id}"]`);
        
        if (text && newText) text.innerHTML = newText.innerHTML;
      });

      this.updateSizeFilterStates();
    }

    updateSizeFilterStates() {
      const params = new URLSearchParams(this.searchParamsPrev);
      
      this.querySelectorAll('.js-size-filter-input').forEach(input => {
        const isChecked = params.has(input.name, input.value);
        const label = input.closest('label');
        
        input.checked = isChecked;
        label?.classList.toggle('active', isChecked);
      });
    }

    async updateSizeFilterOnly(params) {
      if (!this.els.sizeFilter) return;

      try {
        const url = `${this.dataset.urlHandle}?${params}`;
        const response = await fetch(url, { 
          headers: { 'Content-Type': 'text/plain' } 
        });
        const html = await response.text();
        const dom = new DOMParser().parseFromString(html, 'text/html');
        const newFilter = dom.querySelector('.js-top-filter');
        
        if (newFilter) {
          this.els.sizeFilter.innerHTML = newFilter.innerHTML;
          this.setupSizeFilter();
        }
      } catch (error) {
        console.error('Size filter update error:', error);
      }
    }

    updateSizeFilterSection(dom) {
      const newFilter = dom.querySelector('.js-top-filter');
      const current = this.querySelector('.js-top-filter');
      
      if (newFilter && current) {
        if (current.dataset.filterType !== newFilter.dataset.filterType || 
            current.innerHTML !== newFilter.innerHTML) {
          current.outerHTML = newFilter.outerHTML;
          setTimeout(() => this.setupSizeFilter(), 100);
        }
      } else if (newFilter && !current) {
        const insertPoint = this.querySelector('.collection__container .q-box');
        if (insertPoint) {
          insertPoint.insertAdjacentHTML('beforebegin', newFilter.outerHTML);
          setTimeout(() => this.setupSizeFilter(), 100);
        }
      } else if (!newFilter && current) {
        current.remove();
      }
    }

    updateCountActive(dom) {
      const newCount = dom.querySelector('.js-count-active-filter');
      if (newCount) {
        this.lists.countActive.forEach(el => el.innerHTML = newCount.innerHTML);
      }
    }

    // Helper methods
    setLoading(state) {
      this.els.grid?.classList.toggle('is-loading', state);
      this.els.list?.classList.toggle('is-loading', state);
    }

    scrollToCollection() {
      (this.els.grid || this.els.list)?.scrollIntoView({ behavior: 'smooth' });
    }

    clearFilterCache() {
      this.setupClearFilters();
      this.setupRemoveTags();
    }

    refreshExternalScripts() {
      if (window.updateLazyLoad) window.updateLazyLoad();
      if (window.StampedFn) window.StampedFn.reloadUGC();
    }

    createSearchParams(form) {
      const formData = new FormData(form);
      const params = new URLSearchParams(formData);

      params.append('section_id', this.dataset.id);

      if (!params.get('filter.v.price.gte') || params.get('filter.v.price.gte') == '0') {
        params.delete('filter.v.price.gte');
      }
      if (!params.get('filter.v.price.lte') || 
          params.get('filter.v.price.lte') == this.els.priceMax?.max) {
        params.delete('filter.v.price.lte');
      }

      if (this.dataset.currentType) {
        params.append('q', this.dataset.currentType);
      }

      return params.toString();
    }

    clearSearchParams(params) {
      const p = new URLSearchParams(params);
      p.delete('section_id');
      return p.toString();
    }

    updateURL(params) {
      const p = new URLSearchParams(params);
      p.delete('section_id');
      const stateParams = p.toString();
      
      history.pushState({}, '', 
        `${this.dataset.urlHandle}${stateParams && '?'.concat(stateParams)}`
      );
    }

    // Filter management
    toggleClearButton() {
      const btn = this.querySelector('.js-filter-clear-desktop');
      let defaultParams = `sort_by=${this.els.defaultSort?.value}`;
      
      if (this.dataset.currentType) defaultParams += `&q=${this.dataset.currentType}`;
      
      btn?.classList.toggle('hidden', 
        !this.searchParamsPrev || this.searchParamsPrev === defaultParams
      );
    }

    setupClearFilters() {
      const btns = this.querySelectorAll('.js-filter-clear, .js-filter-clear-desktop');
      
      btns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.clearAllFilters();
          
          if (btn.dataset.hideOnClick) {
            btn.classList.add('hidden');
          } else {
            this.toggleDrawer('remove');
          }
        });
      });
    }

    clearAllFilters() {
      this.renderPage('');
      if (this.els.defaultSort) this.els.defaultSort.checked = true;
      
      this.lists.inputs.forEach(input => {
        if (input.type === 'checkbox') input.checked = false;
      });
      
      this.resetPriceInputs();
      this.updateAllFilterCounts();
    }

    setupRemoveTags() {
      this.querySelectorAll('.js-remove-filter-tag').forEach(tag => {
        tag.addEventListener('click', async () => {
          let url = this.searchParamsPrev || window.location.search;
          if (url.charAt(0) === '?') url = url.substring(1);
          
          const params = new URLSearchParams(url);
          params.delete(tag.dataset.paramName, tag.dataset.value);
          params.delete('page');
          
          await this.renderPage(params.toString());
          this.updateAllFilterCounts();
        });
      });
    }

    updateAllFilterCounts() {
      this.lists.filters.forEach(filter => this.showFilterCount(filter));
    }

    showFilterCount(filter) {
      const checked = filter.querySelectorAll('.js-filter-input:checked');
      let count = 0;
      let text = '';

      checked.forEach((input, i) => {
        if (!input.classList.contains('js-collapsed')) {
          count++;
          text += `${input.value}${i < checked.length - 1 ? ', ' : ''}`;
        }
      });

      const selectedText = filter.querySelector('.js-filter-selection');
      const qtyLabel = filter.querySelector('.js-filter-qty');
      
      if (qtyLabel) {
        qtyLabel.textContent = count > 0 ? count : '';
        
        const categoryQty = this.querySelector(
          `.js-filter-qty[data-filter="category-${qtyLabel.dataset.filter}"]`
        );
        
        if (categoryQty) {
          categoryQty.textContent = count > 0 ? `(${count})` : '';
          categoryQty.style.display = count > 0 ? 'inline-block' : 'none';
          categoryQty.parentElement.classList.toggle('is-active', count > 0);
        }
      }
      
      if (selectedText) {
        selectedText.innerHTML = count > 0 ? text : selectedText.dataset.all;
      }
    }

    // Filter drawer
    setupFilterDrawer() {
      if (!this.els.drawer) return;

      this.els.drawerOpen?.addEventListener('click', () => this.toggleDrawer());

      [this.els.drawerToggle, this.els.overlay, this.els.close].forEach(el => {
        el?.addEventListener('click', () => {
          this.toggleDrawer();
          this.els.drawerToggle?.classList.toggle('is-hide');
        });
      });
    }

    toggleDrawer(action = 'toggle') {
      this.els.drawer?.classList[action]('is-open');
      this.els.overlay?.classList[action]('is-open');
      this.classList[action]('is-zindex');
    }

    // Filter toggles
    setupFilterToggles() {
      this.lists.toggles.forEach(btn => {
        btn.addEventListener('click', () => {
          btn.parentElement.classList.toggle('is-open');
        });
      });
    }

    setupShowMore() {
      this.lists.showMore.forEach(btn => {
        btn.addEventListener('click', () => {
          const list = btn.previousElementSibling;
          const isShow = !btn.classList.contains('is-show');
          
          if (isShow) {
            list.querySelectorAll('li.hidden').forEach(li => {
              if (!li.classList.contains('js-filter-collapsed')) {
                li.classList.remove('hidden');
              }
            });
          } else {
            list.querySelectorAll('li').forEach((li, i) => {
              if (i > 6) li.classList.add('hidden');
            });
          }
          
          btn.classList.toggle('is-show', isShow);
          
          const search = btn.parentElement.querySelector('.js-filter-search');
          if (search) search.value = '';
        });
      });
    }

    updateFilterLabels() {
      this.lists.filters.forEach(filter => {
        filter.addEventListener('change', (e) => {
          if (e.target.classList.contains('js-filter-input')) {
            this.showFilterCount(filter);
          }
        });
      });

      this.lists.filters.forEach(filter => this.showFilterCount(filter));
    }

    autoOpenFilters() {
      if (!this.lists.filters || window.matchMedia('(max-width: 960px)').matches) return;
      
      this.lists.filters.forEach((filter, i) => {
        if (i <= 1) filter.classList.add('is-open');
      });
    }

    // Price range
    setupPriceRange() {
      this.lists.priceInputs.forEach(input => {
        input?.addEventListener('input', () => {
          const { min, max } = this.getPriceRange();
          
          this.els.priceMin.value = min;
          this.els.priceMax.value = max;

          const left = (min * 100) / input.max;
          const right = 100 - (max * 100) / input.max;

          input.parentElement.style.setProperty('--left', `${left}%`);
          input.parentElement.style.setProperty('--right', `${right}%`);

          if (this.els.priceLabelMin) this.els.priceLabelMin.textContent = min;
          if (this.els.priceLabelMax) this.els.priceLabelMax.textContent = max;
        });
      });

      [this.els.priceMin, this.els.priceMax].forEach(input => {
        input?.addEventListener('input', () => {
          const range = this.querySelector('.js-price-inputs');
          let min = this.els.priceMin.value || 0;
          let max = this.els.priceMax.value || this.els.priceMax.max;

          let left = Math.max(0, (min * 100) / input.max);
          let right = Math.max(0, Math.min(100, 100 - (max * 100) / input.max));

          range.style.setProperty('--left', `${left}%`);
          range.style.setProperty('--right', `${right}%`);

          this.lists.priceInputs[0].value = min;
          this.lists.priceInputs[1].value = max;

          if (this.els.priceLabelMin) this.els.priceLabelMin.textContent = min;
          if (this.els.priceLabelMax) this.els.priceLabelMax.textContent = max;
        });
      });
    }

    resetPriceInputs() {
      [this.els.priceLabelMin, this.els.priceLabelMax].forEach(label => {
        if (label) label.innerHTML = label.dataset.value;
      });

      this.els.priceMin.value = this.els.priceMin.min;
      this.els.priceMax.value = this.els.priceMax.max;

      this.lists.priceInputs.forEach((input, i) => {
        input.value = i === 0 ? '0' : input.max;
      });

      const range = this.querySelector('.js-price-inputs');
      range.style.setProperty('--left', '0%');
      range.style.setProperty('--right', '0%');
    }

    getPriceRange() {
      const prices = Array.from(this.lists.priceInputs).map(i => i.value).sort((a, b) => a - b);
      return { min: prices[0] || 0, max: prices[1] };
    }

    // Search
    setupSearch() {
      if (!this.els.search) return;

      this.els.search.addEventListener('input', debounce(() => {
        const search = this.els.search.value.toUpperCase();
        let shown = 0;

        this.lists.productTypes.forEach(item => {
          const matches = item.dataset.filter.toUpperCase().includes(search);
          const shouldShow = (!search || matches) && shown < 6;
          
          item.classList.toggle('hidden', !shouldShow);
          if (shouldShow) shown++;
        });
      }, 100));

      this.els.search.addEventListener('input', () => {
        const hasValue = !!this.els.search.value;
        this.els.clearInput?.classList.toggle('is-hidden', !hasValue);
        this.els.clearInput?.previousElementSibling?.classList.toggle('is-hidden', hasValue);
      });

      this.els.clearInput?.addEventListener('click', () => {
        this.els.search.value = '';
        this.els.clearInput.classList.add('is-hidden');
        this.els.clearInput.previousElementSibling.classList.remove('is-hidden');
        this.lists.productTypes.forEach(item => item.classList.remove('hidden'));
      });
    }

    // Sort
    setupSortBy() {
      if (!this.els.sortLabel) return;

      this.lists.sortInputs.forEach(input => {
        input.addEventListener('input', () => {
          if (input.dataset.name) {
            this.els.sortLabel.innerHTML = input.dataset.name;
          }
        });
      });
    }

    // Size filter
    setupSizeFilter() {
      const container = this.querySelector('.js-top-filter');
      if (!container) return;

      const handler = (e) => {
        if (!e.target.classList.contains('js-size-filter-input')) return;

        const allInputs = Array.from(this.querySelectorAll('.js-size-filter-input'));
        const params = new URLSearchParams(window.location.search);

        allInputs.forEach(i => params.delete(i.name, i.value));
        
        allInputs.filter(i => i.checked).forEach(i => {
          params.append(i.name, i.value);
          i.closest('label')?.classList.add('active');
        });

        allInputs.filter(i => !i.checked).forEach(i => {
          i.closest('label')?.classList.remove('active');
        });

        params.delete('page');
        this.renderPage(params.toString(), e);
      };

      container.removeEventListener('change', handler);
      container.addEventListener('change', handler);
    }

    // Infinite scroll & load more
    setupInfiniteScroll() {
      if ('scrollRestoration' in history) window.history.scrollRestoration = 'manual';

      this.loadSecondPageWhenScrolling();
      this.cachePreviousPosition();
      this.restorePreviousPosition();

      this.els.loadMore?.addEventListener('click', () => this.loadMoreHtml(true));
      this.els.loadPrev?.addEventListener('click', () => this.loadMoreHtml(true, false));
    }

    async loadSecondPageWhenScrolling() {
      const anchor = this.querySelectorAll('.js-product-card')[this.productPerPage - 8] || 
                     this.els.loadMore;

      if (!anchor) return;

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach(async (entry) => {
            if (entry.intersectionRatio > 0) {
              await this.loadMoreHtml(true);
              obs.disconnect();
            }
          });
        },
        { root: null, rootMargin: '0px', threshold: 0.01 }
      );

      observer.observe(anchor);
    }

    restorePreviousPosition() {
      const cached = sessionStorage.getItem(window.location.href);
      if (!cached) return;

      const card = this.querySelector(`.js-product-card[data-id="${cached}"]`);
      card?.scrollIntoView();
    }

    cachePreviousPosition() {
      const links = this.querySelectorAll('.js-product-card a, .product-card-horizontal a');
      
      links.forEach(link => {
        link.addEventListener('click', () => {
          const id = link.closest('.js-product-card')?.dataset.id;
          if (id) sessionStorage.setItem(window.location.href, id);
        });
      });
    }

    async loadMoreHtml(cache = true, loadNext = true) {
      const { nextPage, pageURL } = this.getLoadMoreUrl(loadNext);

      if (loadNext && nextPage > this.maxPage) {
        return this.els.loadContainer?.classList.add('hidden');
      }

      this.setLoading(true);
      const btn = loadNext ? this.els.loadMore : this.els.loadPrev;
      btn?.classList.add('is-loading');

      try {
        const response = await fetch(pageURL);
        const html = await response.text();
        const dom = new DOMParser().parseFromString(html, 'text/html');

        const gridHtml = dom.querySelector('.js-collection-grid');
        const listHtml = dom.querySelector('.js-list-view');

        const isEmpty = gridHtml?.classList.contains('is-empty') || 
                       listHtml?.classList.contains('is-empty');
        if (isEmpty) cache = false;

        const position = loadNext ? 'beforeend' : 'afterbegin';

        if (this.els.grid && gridHtml && !gridHtml.classList.contains('is-empty')) {
          this.els.grid.insertAdjacentHTML(position, gridHtml.innerHTML);
        }

        if (this.els.list && listHtml && !listHtml.classList.contains('is-empty')) {
          this.els.list.insertAdjacentHTML(position, listHtml.innerHTML);
        }

        if (loadNext) {
          this.currentPage++;
          this.currentProductQty = nextPage === this.maxPage ? 
            this.maxProduct : 
            this.currentProductQty + this.productPerPage;
        } else {
          this.previousPages--;
          this.currentProductQty += this.productPerPage;
          if (this.previousPages === 1) {
            this.els.loadPrev?.classList.add('hidden');
          }
        }

        this.updateLoadMoreUI();
        this.callUpdateMedia();

        if (cache) history.replaceState({}, '', pageURL);
        this.cachePreviousPosition();

      } catch (error) {
        console.error('Load More Error:', error);
      } finally {
        this.refreshExternalScripts();
        btn?.classList.remove('is-loading');
        this.setLoading(false);

        if (loadNext && nextPage >= this.maxPage) {
          this.els.loadContainer?.classList.add('hidden');
        }
      }
    }

    getLoadMoreUrl(loadNext = true) {
      const params = new URLSearchParams(this.searchParamsPrev);
      const currentPage = loadNext ? this.currentPage + 1 : this.previousPages - 1;

      params.has('page') ? params.set('page', currentPage) : params.append('page', currentPage);

      return {
        previousPage: this.previousPages - 1,
        nextPage: this.currentPage + 1,
        pageURL: `${this.dataset.url}?${params.toString()}`
      };
    }

    updateLoadMoreUI() {
      if (!this.els.loadContainer) return;

      const progress = (this.currentProductQty / this.maxProduct) * 100;

      if (this.els.currentQty) this.els.currentQty.textContent = this.currentProductQty;
      if (this.els.totalQty) this.els.totalQty.textContent = this.maxProduct;
      if (this.els.processBar) this.els.processBar.style.setProperty('--width', `${progress}%`);

      if (this.maxPage > this.currentPage) {
        this.els.loadContainer.classList.remove('hidden');
      }

      if (this.els.qtyInfo) {
        const label = this.maxProduct <= 1 ? 
          window.productQuantity?.one : 
          window.productQuantity?.others;
        this.els.qtyInfo.innerHTML = `${this.maxProduct} ${label || ''}`;
      }
    }

    // Media updates
    callUpdateMedia() {
      const swatchFilter = [];
      const params = new URLSearchParams(this.searchParamsPrev);
      const swatchSettings = this.dataset.swatches?.toLowerCase();

      if (swatchSettings && params) {
        params.forEach((value, key) => {
          if (key.startsWith('filter.v.option')) {
            const optionName = key.replace('filter.v.option.', '');
            if (swatchSettings.includes(optionName)) {
              swatchFilter.push(value);
            }
          }
        });
      }

      if (this.dataset.currentType) {
        this.dataset.currentType.split(' ').forEach(text => swatchFilter.unshift(text));
      }

      requirejs(['pubsub'], () => {
        publishEvent(PUB_SUB_EVENTS.filterSwatch, {
          source: 'collection',
          data: { swatchList: swatchFilter }
        });
      });
    }

    // Categories carousel
    setupCategories() {
      requirejs(['swiper'], (Swiper) => {
        this.categoriesCarousel = new Swiper(`.js-categories-carousel-${this.dataset.id}`, {
          grabCursor: false,
          lazy: true,
          resistanceRatio: 0.4,
          speed: 300,
          loop: false,
          slidesPerView: 'auto',
          spaceBetween: 4,
          navigation: {
            nextEl: `.js-swiper-next-${this.dataset.id}`,
            prevEl: `.js-swiper-prev-${this.dataset.id}`
          }
        });
      });

      this.querySelectorAll('.js-filter-category').forEach(category => {
        category.addEventListener('click', () => {
          this.toggleDrawer();

          const index = category.dataset.index;
          const tab = this.querySelector(`.js-filter[data-index='${index}']`);

          this.lists.filters.forEach(f => f.classList.remove('is-open'));

          if (tab) {
            tab.classList.add('is-open');
            tab.focus();
          }
        });
      });
    }

    // Sticky filter button on mobile
    stickyFilterButton() {
      if (!this.els.drawerToggle) return;

      window.addEventListener('scroll', () => {
        const shouldShow = window.scrollY >= 800 && 
                          window.innerWidth <= 560 && 
                          !this.els.drawer?.classList.contains('is-open');
        
        if (shouldShow) {
          this.els.drawerToggle.classList.remove('is-hide');
          this.els.drawerToggle.classList.add('is-fixed');
        } else {
          this.els.drawerToggle.classList.remove('is-fixed');
        }
      });
    }
  }

  customElements.define('collection-page', CollectionPage);
}